/**
 * SEO Utility functions for the website
 */

export const BASE_URL = 'https://homeworkguy.org';

/**
 * Generates a canonical URL for the given path
 * @param path - The path to generate canonical URL for (e.g., '/services')
 * @returns Full canonical URL (e.g., 'https://homeworkguy.org/services')
 */
export const generateCanonicalUrl = (path: string = ''): string => {
  // Handle root path specially
  if (path === '/' || path === '') {
    return BASE_URL;
  }
  
  // Ensure path starts with a slash
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // Remove query parameters and hash fragments for canonical URLs
  const cleanPath = normalizedPath.split('?')[0].split('#')[0];
  
  // Combine base URL with normalized path
  return `${BASE_URL}${cleanPath}`;
};

/**
 * Get current page path for canonical URL
 * Works in both client-side and server-side contexts
 */
export const getCurrentPagePath = (): string => {
  // In client-side context
  if (typeof window !== 'undefined') {
    // Remove query parameters and hash fragments for canonical URLs
    return window.location.pathname.split('?')[0].split('#')[0];
  }
  
  // Default for server-side context (will be updated on client)
  return '/';
};

/**
 * Identify potential duplicate URLs that should share the same canonical
 * @param path The path to check
 * @returns The canonical version of the path
 */
export const standardizePath = (path: string): string => {
  // Standardize specific patterns known to create duplicates
  
  // Remove trailing index.html
  if (path.endsWith('/index.html')) {
    path = path.substring(0, path.length - 11);
  }
  
  // Handle common alternate URL patterns
  if (path.match(/\/page\/\d+\/?$/)) {
    // Paginated content should generally canonicalize to the first page
    path = path.replace(/\/page\/\d+\/?$/, '');
  }

  // Handle potential URL variations (customize based on your site structure)
  const lowercasePath = path.toLowerCase();
  
  // For example, if you have both uppercase and lowercase URLs, standardize to lowercase
  return lowercasePath;
};

/**
 * Check if a page needs trailing slash normalization
 * @param url The full URL to check
 */
export const needsTrailingSlashFix = (url: string): boolean => {
  // Check if URL ends with a file extension (e.g., .html, .php)
  const hasFileExtension = /\.\w+$/.test(url);
  
  // URLs with file extensions don't need trailing slashes
  if (hasFileExtension) {
    return false;
  }
  
  // For other URLs, we'll normalize to having a trailing slash
  return !url.endsWith('/');
};

/**
 * Normalize URL with trailing slash if needed
 * @param url The URL to normalize
 */
export const normalizeUrl = (url: string): string => {
  // First standardize the path to handle potential duplicates
  const standardizedUrl = url.replace(BASE_URL, '');
  const standardizedPath = standardizePath(standardizedUrl);
  const fullUrl = `${BASE_URL}${standardizedPath}`;
  
  // Then apply trailing slash normalization
  if (needsTrailingSlashFix(fullUrl) && !fullUrl.endsWith('/')) {
    return `${fullUrl}/`;
  }
  
  return fullUrl;
};

/**
 * Check if two URLs should be considered duplicates
 * @param url1 First URL to compare
 * @param url2 Second URL to compare
 */
export const areDuplicateUrls = (url1: string, url2: string): boolean => {
  // Normalize both URLs
  const normalizedUrl1 = normalizeUrl(url1);
  const normalizedUrl2 = normalizeUrl(url2);
  
  // Compare the normalized URLs
  return normalizedUrl1 === normalizedUrl2;
};

/**
 * Handles canonicalization for blog URLs with parameters
 * Ensures all blog URLs with parameters (category, tag, etc.) 
 * point to the main blog URL as canonical
 */
export const getBlogCanonicalUrl = (path: string, baseUrl: string = 'https://homeworkguy.org'): string => {
  // Check if this is a blog URL with parameters
  if (path.startsWith('/blog') && path.includes('?')) {
    // Always return the main blog URL as canonical
    return `${baseUrl}/blog`;
  }
  
  // For blog post URLs (individual articles), keep the full path
  if (path.startsWith('/blog/')) {
    return `${baseUrl}${path}`;
  }
  
  // For all other URLs, just combine base with path
  return `${baseUrl}${path}`;
};

/**
 * Check if a page is a key page that should always be indexed
 * @param path The current path
 * @returns true if the page should always be indexed
 */
export const isKeyPage = (path: string): boolean => {
  // List of paths that should always be indexed
  const keyPaths = ['/', '/blog', '/services', '/contact', '/how-it-works'];
  
  // Clean up the path for comparison
  const cleanPath = path.replace(/\/$/, '');
  
  return keyPaths.includes(cleanPath) || keyPaths.includes(cleanPath + '/');
};

export default {
  BASE_URL,
  generateCanonicalUrl,
  getCurrentPagePath,
  needsTrailingSlashFix,
  normalizeUrl,
  standardizePath,
  areDuplicateUrls,
  getBlogCanonicalUrl,
  isKeyPage
}; 